import { createContext, useContext, useEffect, useState } from "react";
import type { AuthContextType, Role } from "@/types/auth";
import { Role as RoleEnum } from "@/types/auth";
import type { AppUser, AuthSession } from "@/lib/authApi";
import {
  requestOtp as apiRequestOtp,
  verifyOtp as apiVerifyOtp,
  logout as apiLogout,
  loadSession,
  clearSession,
  refreshAccessToken,
  isSameSession,
  AUTH_CLEARED_EVENT,
  AUTH_STORAGE_KEY,
  TOKEN_REFRESH_MARGIN_MS,
} from "@/lib/authApi";
import { apiFetch } from "@/lib/apiClient";
import { unsubscribeDeviceOnSignOut } from "@/lib/push-notifications";
import { ROLE_HIERARCHY } from "@/lib/roles";
import {
  storeUserLocationId,
  clearCurrentLocationId,
} from "@/lib/locationUtils";
import type { Location } from "@/types/order";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [location, setLocation] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  const fetchLocations = async (): Promise<Location[]> => {
    try {
      const response = await apiFetch("/locations/");
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  };

  const applyUser = async (session: AuthSession) => {
    if (!isSameSession(session)) return;
    const appUser = session.user;
    setUser(appUser);
    setDisplayName(appUser.display_name ?? null);
    const userRole = (appUser.role as Role) ?? RoleEnum.FLEURISTE;
    setRole(userRole);
    setLocation(appUser.location ?? null);

    if (
      (userRole === RoleEnum.BOUTIQUE ||
        userRole === RoleEnum.FLEURISTE ||
        userRole === RoleEnum.ATELIER) &&
      appUser.location
    ) {
      const locations = await fetchLocations();
      if (!isSameSession(session)) return;
      await storeUserLocationId(appUser.location, locations);
    }
  };

  const clearUser = () => {
    setUser(null);
    setDisplayName(null);
    setRole(null);
    setLocation(null);
    clearCurrentLocationId();
  };

  const resetAuthState = () => {
    setOtpSent(false);
    setPhone(null);
    clearUser();
  };

  const syncSessionState = async () => {
    const session = loadSession();
    if (!session) {
      resetAuthState();
      return;
    }

    setError(null);
    setOtpSent(false);
    setPhone(session.user.phone ?? null);
    await applyUser(session);
  };

  // ------------------------------------------------------------------
  // Restauration de session au chargement
  // ------------------------------------------------------------------
  useEffect(() => {
    const restoreSession = async () => {
      setLoading(true);
      try {
        const session = loadSession();
        if (!session) {
          resetAuthState();
          return;
        }

        // If close to expiry, try a refresh before restoring
        if (Date.now() > session.tokens.expiresAt - TOKEN_REFRESH_MARGIN_MS) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            if (!loadSession()) resetAuthState();
            return;
          }
        }

        // Re-read the (possibly refreshed) session
        const current = loadSession();
        if (current) {
          await syncSessionState();
        } else {
          resetAuthState();
        }
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Listen for same-tab auth clears and cross-tab storage updates.
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleAuthCleared = () => {
      resetAuthState();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY) return;
      void syncSessionState();
    };

    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
      window.removeEventListener("storage", handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Auth actions
  // ------------------------------------------------------------------

  const signInWithPhone = async (phoneNumber: string) => {
    try {
      setError(null);
      setLoading(true);
      await apiRequestOtp(phoneNumber);
      setPhone(phoneNumber);
      setOtpSent(true);
    } catch (err) {
      // The server always answers success for a valid phone (no enumeration),
      // so only transport or validation errors reach this branch.
      setError((err as Error).message ?? "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (phoneNumber: string, token: string) => {
    try {
      setError(null);
      setLoading(true);
      const session = await apiVerifyOtp(phoneNumber, token);
      setOtpSent(false);
      setPhone(null);
      await applyUser(session);
    } catch (err) {
      setError((err as Error).message ?? "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    const session = loadSession();
    try {
      setError(null);
      if (session) {
        // Detach this device from the user's push subscriptions while the
        // token is still valid. Best effort: a failure must not block logout.
        try {
          await unsubscribeDeviceOnSignOut();
        } catch (pushErr) {
          console.warn("[Auth] Push unsubscribe on sign-out failed:", pushErr);
        }
        if (!isSameSession(session)) return;
        await apiLogout(loadSession()!.tokens.refresh);
        if (loadSession()) return;
      } else {
        clearSession();
      }
      setOtpSent(false);
      setPhone(null);
      clearUser();
    } catch (err) {
      if (session && loadSession() && !isSameSession(session)) return;
      setError((err as Error).message ?? "Une erreur est survenue");
    }
  };

  const resetAuth = () => {
    setError(null);
    setOtpSent(false);
    setPhone(null);
  };

  const getAccessToken = async (): Promise<string | null> => {
    const session = loadSession();
    if (!session) return null;

    // Refresh proactively when close to expiry
    if (Date.now() > session.tokens.expiresAt - TOKEN_REFRESH_MARGIN_MS) {
      const refreshed = await refreshAccessToken();
      return refreshed?.access ?? null;
    }

    return session.tokens.access;
  };

  const hasRole = (requiredRole: Role): boolean => {
    if (!role) return false;
    const userLevel = ROLE_HIERARCHY[role as Role] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 0;
    return userLevel >= requiredLevel;
  };

  const value: AuthContextType = {
    user,
    loading,
    phone,
    displayName,
    role,
    location,
    signInWithPhone,
    verifyOtp,
    signOut,
    resetAuth,
    getAccessToken,
    error,
    otpSent,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
